import React, { useEffect, useMemo, useState } from "react";
import Button from "components/ui/Button";
import Select from "components/ui/Select";
import Icon from "components/AppIcon";
import useAuth from "hooks/useAuth";
import { useLocationContext } from "context/LocationContext";
import {
  useCreateAssessmentSetup,
  useGetAssessmentByPeriod,
  useGetIndicatorsSummary,
} from "service/setup.service";
import { getRegionCodeForLocation, getStoredAssessmentSetupsForLocation } from "../monitoringWorkflow";

const QUARTER_OPTIONS = [
  { label: "Q1", value: "Q1" },
  { label: "Q2", value: "Q2" },
  { label: "Q3", value: "Q3" },
  { label: "Q4", value: "Q4" },
];

const SETUP_STATUS_OPTIONS = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const MOCK_ASSESSMENT_DATA = [
  {
    assessmentSetupId: 1,
    regionCode: "",
    year: "2025",
    quarter: "Q1",
    status: "Active",
    indicators: [1, 2],
    indicatorLabels: ["GI-CPIME", "GI-PP"],
    createdAt: "2026-03-11T15:39:12.639533",
    updatedAt: null,
    createdBy: "msow",
    updatedBy: "msow",
    indicator: {
      indicatorId: 1,
      name: "COORDINATION, PLAN IMPLEMENTATION, AND M&E",
      code: "GI-CPIME",
      status: "Active",
      description:
        "COORDINATION, PLAN IMPLEMENTATION, AND M&E (25) (PLANNING AND RELATED ISSUES - 34 MARKS)",
      createdAt: "2026-03-10T12:06:04.214356",
      updatedAt: null,
      createdBy: "msow",
      updatedBy: "msow",
    },
  },
  {
    assessmentSetupId: 2,
    regionCode: "",
    year: "2025",
    quarter: "Q1",
    status: "Active",
    indicators: [1, 2],
    indicatorLabels: ["GI-CPIME", "GI-PP"],
    createdAt: "2026-03-11T15:39:12.639533",
    updatedAt: null,
    createdBy: "msow",
    updatedBy: "msow",
    indicator: {
      indicatorId: 2,
      name: "PHYSICAL PLANNING (5 MARKS)",
      code: "GI-PP",
      status: "Active",
      description: "PHYSICAL PLANNING (5 MARKS).",
      createdAt: "2026-03-10T16:25:31.239468",
      updatedAt: null,
      createdBy: "msow",
      updatedBy: "msow",
    },
  },
];

function normalizeBackendRows(response) {
  const payload = response?.data;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function toSafeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getIndicatorIdsFromRows(rows) {
  const items = Array.isArray(rows) ? rows : [];
  const ids = new Set();

  items.forEach((item) => {
    const directIndicatorId = toSafeNumber(item?.indicatorId || item?.indicator?.indicatorId);
    if (directIndicatorId) {
      ids.add(directIndicatorId);
    }

    if (!Array.isArray(item?.indicators)) {
      return;
    }

    item.indicators.forEach((value) => {
      const parsed = toSafeNumber(value);
      if (parsed) {
        ids.add(parsed);
      }
    });
  });

  return Array.from(ids);
}

function getRowsForPeriod(rows, regionCode, year, quarter) {
  const items = Array.isArray(rows) ? rows : [];

  return items.filter(
    (item) =>
      String(item?.regionCode || "").toLowerCase() === String(regionCode || "").toLowerCase() &&
      String(item?.year || "") === String(year || "") &&
      String(item?.quarter || "") === String(quarter || ""),
  );
}

function normalizeRecentAssessmentRows(rows, currentRegionCode) {
  const allRows = Array.isArray(rows) ? rows : [];
  const normalizedRegionCode = String(currentRegionCode || "").toLowerCase();
  const regionRows = allRows.filter(
    (item) => String(item?.regionCode || "").toLowerCase() === normalizedRegionCode,
  );
  const sourceRows = regionRows.length > 0 ? regionRows : allRows;
  const groupedRows = new Map();

  sourceRows.forEach((item, index) => {
    const key =
      item?.assessmentSetupId ||
      `${item?.regionCode || currentRegionCode}:${item?.year || "year"}:${item?.quarter || "quarter"}:${index}`;

    if (!groupedRows.has(key)) {
      groupedRows.set(key, {
        assessmentSetupId: item?.assessmentSetupId || key,
        regionCode: item?.regionCode || currentRegionCode || "-",
        year: item?.year || "",
        quarter: item?.quarter || "",
        status: item?.status || "Active",
        indicators: [],
        createdAt: item?.createdAt || "",
      });
    }

    const current = groupedRows.get(key);
    const indicatorValues = [];

    if (Array.isArray(item?.indicatorLabels)) {
      indicatorValues.push(...item.indicatorLabels.map((value) => String(value)));
    } else if (Array.isArray(item?.indicators)) {
      item.indicators.forEach((value) => {
        const normalized = String(value || "").trim();
        if (normalized && Number.isNaN(Number(normalized))) {
          indicatorValues.push(normalized);
        }
      });
    }

    if (item?.indicator?.code) {
      indicatorValues.push(item.indicator.code);
    } else if (item?.indicator?.name) {
      indicatorValues.push(item.indicator.name);
    }

    indicatorValues.forEach((value) => {
      if (value && !current.indicators.includes(value)) {
        current.indicators.push(value);
      }
    });
  });

  return Array.from(groupedRows.values())
    .sort((left, right) => String(right.createdAt).localeCompare(String(left.createdAt)))
    .slice(0, 8);
}

function RegionRequiredState() {
  return (
    <div className="main-content">
      <div className="card">
        <h2 className="mb-2 text-xl font-semibold text-foreground">Region Selection Required</h2>
        <p className="text-muted-foreground">
          Performance Setup is done at regional level. Select a region from the sidebar, then open this module.
        </p>
      </div>
    </div>
  );
}

export default function PerformanceAssessmentModuleAdapter() {
  const { currentLocation } = useLocationContext();
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const regionCode = getRegionCodeForLocation(currentLocation);

  const yearOptions = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) => {
        const year = currentYear - 3 + index;
        return { label: String(year), value: String(year) };
      }),
    [currentYear],
  );

  const defaultYear =
    yearOptions.find((option) => option.value === String(currentYear)) || yearOptions[yearOptions.length - 1];

  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedQuarter, setSelectedQuarter] = useState(QUARTER_OPTIONS[0]);
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const [formError, setFormError] = useState("");
  const [activeTab, setActiveTab] = useState("builder");
  const localAssessmentSetups = useMemo(() => {
    const storedRows = getStoredAssessmentSetupsForLocation(currentLocation);
    return storedRows.length > 0 ? storedRows : MOCK_ASSESSMENT_DATA;
  }, [currentLocation]);

  const { data: indicators, isLoading: isIndicatorsLoading } = useGetIndicatorsSummary();
  const { mutate: addSetup } = useCreateAssessmentSetup();

  const username = user?.user?.username || user?.user?.fullName || "system";
  const selectedStatus = SETUP_STATUS_OPTIONS[0];

  const { data: assessmentData } = useGetAssessmentByPeriod({
    regionCode,
    year: selectedYear?.value,
    quarter: selectedQuarter?.value,
  });

  const backendIndicatorRows = useMemo(
    () => normalizeBackendRows(indicators),
    [indicators],
  );

  const mockIndicators = useMemo(() => {
    const map = new Map();

    MOCK_ASSESSMENT_DATA.forEach((item) => {
      const indicator = item?.indicator;
      if (!indicator?.indicatorId || map.has(indicator.indicatorId)) {
        return;
      }

      map.set(indicator.indicatorId, {
        indicatorId: indicator.indicatorId,
        name: indicator.name || `Indicator ${indicator.indicatorId}`,
        code: indicator.code || `IND-${indicator.indicatorId}`,
        numberOfSubs: 1,
        indicatorStatus: indicator.status || "Active",
      });
    });

    return Array.from(map.values());
  }, []);

  const indicatorOptions = useMemo(() => {
    if (backendIndicatorRows.length > 0) {
      return backendIndicatorRows;
    }

    return mockIndicators;
  }, [backendIndicatorRows, mockIndicators]);

  const activeIndicators = useMemo(
    () => indicatorOptions.filter((indicator) => indicator.indicatorStatus === "Active"),
    [indicatorOptions],
  );

  const backendPeriodRows = useMemo(
    () => normalizeBackendRows(assessmentData),
    [assessmentData],
  );

  const localPeriodRows = useMemo(
    () => getRowsForPeriod(localAssessmentSetups, regionCode, selectedYear?.value, selectedQuarter?.value),
    [localAssessmentSetups, regionCode, selectedQuarter?.value, selectedYear?.value],
  );

  const currentPeriodRows = useMemo(() => {
    if (backendPeriodRows.length > 0) {
      return backendPeriodRows;
    }

    return localPeriodRows;
  }, [backendPeriodRows, localPeriodRows]);

  const currentPeriodIndicatorIds = useMemo(
    () => getIndicatorIdsFromRows(currentPeriodRows),
    [currentPeriodRows],
  );

  const recentAssessmentRows = useMemo(
    () => normalizeRecentAssessmentRows(localAssessmentSetups, regionCode),
    [localAssessmentSetups, regionCode],
  );

  useEffect(() => {
    setSelectedIndicators(currentPeriodIndicatorIds);
  }, [currentPeriodIndicatorIds]);

  if (currentLocation?.level !== "region") {
    return <RegionRequiredState />;
  }

  const handleSelectAll = () => {
    setFormError("");
    setSelectedIndicators(activeIndicators.map((indicator) => indicator.indicatorId));
  };

  const handleClearSelection = () => {
    setFormError("");
    setSelectedIndicators([]);
  };

  const toggleIndicator = (indicatorId) => {
    setFormError("");
    setSelectedIndicators((current) =>
      current.includes(indicatorId)
        ? current.filter((value) => value !== indicatorId)
        : [...current, indicatorId],
    );
  };

  const handleSaveAssessment = () => {
    setFormError("");

    if (!regionCode) {
      setFormError("Region code is required before saving.");
      return;
    }

    if (selectedIndicators.length === 0) {
      setFormError("Select at least one indicator for this performance setup.");
      return;
    }

    const indicatorLabels = selectedIndicators
      .map((indicatorId) => {
        const indicator = indicatorOptions.find((item) => item.indicatorId === indicatorId);
        return indicator?.code || indicator?.name || String(indicatorId);
      })
      .filter(Boolean);

    const payload = {
      assessmentSetupId: 0,
      regionCode,
      indicators: selectedIndicators,
      year: selectedYear?.value || "",
      quarter: selectedQuarter?.value || "",
      status: selectedStatus?.value || "",
      createdBy: username,
      updatedBy: username,
    };

    // if (USE_LOCAL_SETUP_FALLBACK) {
    //   const localRow = {
    //     assessmentSetupId: Date.now(),
    //     regionCode,
    //     indicators: selectedIndicators,
    //     indicatorLabels,
    //     year: selectedYear?.value || "",
    //     quarter: selectedQuarter?.value || "",
    //     status: selectedStatus?.value || SETUP_STATUS_OPTIONS[0].value,
    //     createdAt: new Date().toISOString(),
    //     createdBy: username,
    //     updatedBy: username,
    //   };

    //   setLocalAssessmentSetups((current) => {
    //     const remainingRows = current.filter(
    //       (item) =>
    //         !(
    //           String(item?.regionCode || "").toLowerCase() === String(regionCode || "").toLowerCase() &&
    //           String(item?.year || "") === String(selectedYear?.value || "") &&
    //           String(item?.quarter || "") === String(selectedQuarter?.value || "")
    //         ),
    //     );

    //     return [localRow, ...remainingRows];
    //   });

    //   setActiveTab("history");
    //   return;
    // }

    addSetup(payload);
  };

  const handleOpenSetup = (setupItem) => {
    const nextYear =
      yearOptions.find((option) => option.value === String(setupItem?.year || "")) || yearOptions[0] || null;
    const nextQuarter =
      QUARTER_OPTIONS.find((option) => option.value === String(setupItem?.quarter || "")) || QUARTER_OPTIONS[0];

    if (nextYear) {
      setSelectedYear(nextYear);
    }

    setSelectedQuarter(nextQuarter);
    setActiveTab("builder");
  };

  return (
    <div className="min-h-full bg-background">
      <div className="main-content space-y-5 pb-6">
        <section className="card p-4 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Performance Setup</p>
              <h2 className="text-lg font-semibold text-foreground">{currentLocation.name}</h2>
              <p className="text-sm text-muted-foreground">
                Regional performance setup for period-based indicator selection.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                <Icon name="MapPin" className="h-3.5 w-3.5" />
                Region Code: {regionCode}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                <Icon name="User" className="h-3.5 w-3.5" />
                {username}
              </span>
            </div>
          </div>
        </section>

        <section className="card p-4 md:p-5">
          <div className="grid gap-3 md:grid-cols-2">
            <Select
              label="Year"
              options={yearOptions}
              value={selectedYear}
              onChange={setSelectedYear}
              placeholder="Select year"
            />
            <Select
              label="Quarter"
              options={QUARTER_OPTIONS}
              value={selectedQuarter}
              onChange={setSelectedQuarter}
              placeholder="Select quarter"
            />
          </div>
        </section>

        <section className="card p-2">
          <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1">
            <button
              type="button"
              onClick={() => setActiveTab("builder")}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "builder"
                  ? "bg-[#16325a] text-white"
                  : "text-muted-foreground hover:bg-background hover:text-foreground"
              }`}
            >
              Create Setup
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "history"
                  ? "bg-[#16325a] text-white"
                  : "text-muted-foreground hover:bg-background hover:text-foreground"
              }`}
            >
              Recent Performance Setups ({recentAssessmentRows.length})
            </button>
          </div>
        </section>

        {activeTab === "builder" ? (
          <section className="card space-y-4 p-4 md:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
              <div>
                <h3 className="text-base font-semibold text-foreground">Period Setup</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Select one or more indicator groups for this region and period.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleSelectAll}>
                  Select All Active
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleClearSelection}>
                  Clear
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/50 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <th className="w-10 px-3 py-2.5">Select</th>
                    <th className="px-3 py-2.5">Indicator Group</th>
                    <th className="px-3 py-2.5">Code</th>
                    <th className="px-3 py-2.5">Number of Indicators</th>
                    <th className="px-3 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {isIndicatorsLoading ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                        Loading indicators...
                      </td>
                    </tr>
                  ) : indicatorOptions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                        No indicator groups found.
                      </td>
                    </tr>
                  ) : (
                    indicatorOptions.map((indicator) => {
                      const isSelected = selectedIndicators.includes(indicator.indicatorId);
                      const isDisabled = indicator.indicatorStatus !== "Active";

                      return (
                        <tr key={indicator.indicatorId} className={isSelected ? "bg-muted/40" : ""}>
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleIndicator(indicator.indicatorId)}
                              disabled={isDisabled}
                              className="h-4 w-4 rounded border border-input bg-background text-[#16325a] focus:ring-2 focus:ring-[#16325a]/30 disabled:opacity-40"
                              aria-label={`Select ${indicator.name}`}
                            />
                          </td>
                          <td className="px-3 py-3 font-medium text-foreground">{indicator.name}</td>
                          <td className="px-3 py-3 text-muted-foreground">{indicator.code}</td>
                          <td className="px-3 py-3 text-muted-foreground">{indicator.numberOfSubs}</td>
                          <td className="px-3 py-3">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                                indicator.indicatorStatus === "Active"
                                  ? "bg-success/10 text-success"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {indicator.indicatorStatus}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
              <p className="text-sm text-muted-foreground">
                Save the selected indicators for {selectedYear?.value} {selectedQuarter?.value}.
              </p>
              <Button type="button" size="sm" onClick={handleSaveAssessment}>
                <Icon name="Save" className="h-4 w-4" />
                Save Performance Setup
              </Button>
            </div>

            {formError ? (
              <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {formError}
              </div>
            ) : null}
          </section>
        ) : null}

        {activeTab === "history" ? (
          <section className="card p-4 md:p-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-base font-semibold text-foreground">Recent Performance Setups</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Latest saved performance setups for quick review and validation.
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setActiveTab("builder")}>
                <Icon name="Plus" className="h-4 w-4" />
                Create New Setup
              </Button>
            </div>

            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/50 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2.5">Setup ID</th>
                    <th className="px-3 py-2.5">Region</th>
                    <th className="px-3 py-2.5">Period</th>
                    <th className="px-3 py-2.5">Indicators</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentAssessmentRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                        No performance setups saved yet.
                      </td>
                    </tr>
                  ) : (
                    recentAssessmentRows.map((item) => (
                      <tr key={item.assessmentSetupId}>
                        <td className="px-3 py-3 text-muted-foreground">{item.assessmentSetupId}</td>
                        <td className="px-3 py-3 text-muted-foreground">{item.regionCode}</td>
                        <td className="px-3 py-3 text-muted-foreground">
                          {item.year} {item.quarter}
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">
                          {Array.isArray(item.indicators) && item.indicators.length > 0
                            ? item.indicators.join(", ")
                            : "-"}
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">{item.status || "-"}</td>
                        <td className="px-3 py-3 text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenSetup(item)}
                          >
                            <Icon name="ClipboardCheck" className="h-4 w-4" />
                            Open Setup
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
